import React from "react";
import { createListTableColumns, ensure, ListTable, renderDateTime, Stack, Text } from "sprint-common-web";

import { DistributionList } from "../../../common/data/lookup-data-providers/DistributionListsDataProvider";
import { DeliveryEndpointType } from "../../../common/types/DeliveryEndpointType";
import { renderDeliveryEndpointType } from "../../../common/utils/renderDeliveryEndpointType";
import { EditDeliveryEndpointButton } from "../edit/EditDeliveryEndpointButton";
import { DeliveryEndpointItem } from "./DeliveryEndpointItem";
import { useDeliveryEndpointListing } from "./useDeliveryEndpointListing";
import { useDeliveryEndpointListingFilters } from "./useDeliveryEndpointListingFilters";

export const DeliveryEndpointListing: React.VFC = () => {
    const { filters, setSort, setPage, setPageSize } = useDeliveryEndpointListingFilters();
    const { items, totalItems, isLoading } = useDeliveryEndpointListing(filters);

    const columns = createListTableColumns<DeliveryEndpointItem>([
        {
            type: "basic",
            key: "name",
            name: "Name",
            minWidth: 60,
            maxWidth: 100,
            isSortable: true
        },
        {
            type: "basic",
            key: "type",
            name: "Type",
            minWidth: 50,
            maxWidth: 80,
            onRender: renderDeliveryEndpointType
        },
        {
            type: "basic",
            key: "details",
            name: "Details",
            minWidth: 200,
            maxWidth: 400,
            onRender: renderDetails
        },
        {
            type: "basic",
            key: "updatedDate",
            name: "Updated",
            minWidth: 150,
            maxWidth: 200,
            onRender: renderDateTime,
            isSortable: true,
            isResizable: false
        },
        {
            type: "buttons",
            key: "actions",
            name: "Actions",
            minWidth: 64,
            maxWidth: 64,
            isResizable: false,
            buttons: [
                {
                    id: "edit",
                    render: EditDeliveryEndpointButton
                }
            ]
        }
    ]);

    return (
        <ListTable
            columns={columns}
            items={items}
            pagination={{
                page: filters.page,
                pageSize: filters.pageSize,
                totalItems,
                onPageChange: setPage,
                onPageSizeChange: setPageSize
            }}
            sort={{ by: filters.sortBy, order: filters.sortOrder }}
            onSortChange={setSort}
            isLoading={isLoading}
        />
    );
};

const renderDetails = (item?: DeliveryEndpointItem) => {
    ensure(item);

    switch (item.details.type) {
        case DeliveryEndpointType.Smartjog:
            return (
                <Stack tokens={{ childrenGap: 5 }}>
                    <Text variant="smallPlus" styles={{ root: { fontWeight: "bold" } }}>
                        Distribution lists:
                    </Text>
                    {renderDistributionListNames(item.details.distributionLists)}
                </Stack>
            );
        case DeliveryEndpointType.Aspera:
            return (
                <Stack tokens={{ childrenGap: 5 }}>
                    <Text variant="smallPlus" styles={{ root: { fontWeight: "bold" } }}>
                        Url:
                    </Text>
                    <Text variant="smallPlus">{item.details.url}</Text>
                    <Text variant="smallPlus" styles={{ root: { fontWeight: "bold" } }}>
                        User:
                    </Text>
                    <Text variant="smallPlus">{item.details.user}</Text>
                    <Text variant="smallPlus" styles={{ root: { fontWeight: "bold" } }}>
                        Ssm private key:
                    </Text>
                    <Text variant="smallPlus">{item.details.ssmPrivateKeyName}</Text>
                    <Text variant="smallPlus" styles={{ root: { fontWeight: "bold" } }}>
                        Workspace:
                    </Text>
                    <Text variant="smallPlus">{item.details.workspace ?? "-"}</Text>
                </Stack>
            );
        case DeliveryEndpointType.Signiant:
            return (
                <Stack tokens={{ childrenGap: 5 }}>
                    <Text variant="smallPlus" styles={{ root: { fontWeight: "bold" } }}>
                        Profile:
                    </Text>
                    <Text variant="smallPlus">{item.details.destinationStorageProfile.name}</Text>
                    <Text variant="smallPlus" styles={{ root: { fontWeight: "bold" } }}>
                        Distribution Email:
                    </Text>
                    <Text variant="smallPlus">{item.details.distributionEmail}</Text>
                </Stack>
            );
    }
};

const renderDistributionListNames = (distributionLists: DistributionList[]) =>
    distributionLists.map((distributionList) => {
        return (
            <Text key={distributionList.id} variant="smallPlus">
                {distributionList.name}
            </Text>
        );
    });
